import { getOrderDocuments, getSignedPurchaseDocuments, isAgreementGroup } from "./order.documents";

const firstRecord = (...values) => values.find(
  (value) => value && typeof value === "object" && !Array.isArray(value) && Object.keys(value).length,
) || {};

const mergeRecords = (...values) => Object.assign({}, ...values.map((value) => firstRecord(value)));
const pickFields = (value, keys) => Object.fromEntries(keys.filter((key) => value?.[key] != null).map((key) => [key, value[key]]));

export function getSubmittedPurchaseSteps(purchase, submitted = {}) {
  const order = purchase.source || purchase;
  const identityKeys = ["registration_type", "ic_no", "legal_first_name", "legal_last_name", "company_name", "company_email", "company_register_no", "tin", "address", "city", "postcode", "state", "buyer_type", "id_front", "id_back", "license_front", "license_back", "company_ssm"];
  const insuranceKeys = ["selling_price", "sum_insured_percent", "sum_insured_amount", "current_ncd", "windscreen_tinted_coverage", "windscreen_tinted_amount", "insurance_company", "correspondence_address", "special_perils", "takaful_insurance", "booklet_version", "ncd_letter", "business_name_card", "parking_address"];
  const identity = mergeRecords(pickFields(order, identityKeys), order.buyer, order.customer, order.registration, order.identity, order.identity_details, submitted.identity, submitted.identity_details);
  identity.registration_type = identity.registration_type || identity.type;
  identity.buyer_type = identity.buyer_type || order.buying_method;
  const finance = mergeRecords(order.financing, order.finance, submitted.finance);
  const insurance = mergeRecords(pickFields(order, insuranceKeys), order.insurance_detail, order.insurance, order.insurance_details, order.insurance_request, submitted.insurance);
  const status = String(order.status?.value || order.status?.name || order.status || "").toLowerCase();
  const counts = { reserved: 0, financing: 1, insurance: 2, delivery: 3, completed: 3, complete: 3, delivered: 3 };
  const completed = counts[status] ?? 0;
  const documents = getOrderDocuments(purchase);
  const signedDocuments = getSignedPurchaseDocuments(purchase, {
    spa: submitted.agreement?.sale_purchase_agreement,
    vso: submitted.finance?.vso_document,
  });
  const identitySaved = Boolean(
    (identity.ic_no || identity.company_register_no) && identity.registration_type && identity.buyer_type,
  );
  const insuranceSaved = Boolean(
    insurance.insurance_company && insurance.sum_insured_amount,
  );
  const upload = (data, key, label) => {
    const value = data[key];
    if (typeof File !== "undefined" && value instanceof File) return [{ file: value, name: value.name, group: key }];
    const entries = Array.isArray(value) ? value : [value];
    return entries.flatMap((item) => {
      const link = typeof item === "string" ? item : item?.url || item?.api_url;
      if (!link) return [];
      try {
        const url = new URL(link, window.location.origin);
        return ["http:", "https:"].includes(url.protocol)
          ? [{ url: url.href, name: item?.display_name || item?.name || label, group: key }]
          : [];
      } catch { return []; }
    });
  };
  const identityUploads = [
    ["id_front", "Front of ID"], ["id_back", "Back of ID"],
    ["license_front", "Driving License Front"], ["license_back", "Driving License Back"],
    ["company_ssm", "Company Full Set SSM"],
  ];
  const isCompany = (identity.registration_type || identity.type) === "company";
  const steps = [
    {
      number: 1, title: "Identity Details", submitted: completed >= 1 || identitySaved || Boolean(submitted.identity || submitted.identity_details),
      data: identity,
      fields: [
        ["registration_type", "Registration Type"],
        ...(isCompany ? [
          ["company_name", "Company Name"], ["company_email", "Company Email"],
          ["company_register_no", "Company Register No"], ["tin", "TIN"],
        ] : [
          ["ic_no", "IC No. / Passport Number"], ["legal_first_name", "Legal First Name"],
          ["legal_last_name", "Legal Last Name"],
        ]),
        ["address", "Address / Unit No"], ["city", "City"], ["postcode", "Postcode"], ["state", "State"],
        ["buyer_type", "Purchase Method"],
      ],
      documents: [
        ...documents.filter((file) => identityUploads.some(([key]) => file.group === key)),
        ...identityUploads.flatMap(([key, label]) => upload(identity, key, label)),
      ],
    },
    {
      number: 2, title: "Financing & VSO", submitted: completed >= 2 || Boolean(signedDocuments.spa && signedDocuments.vso),
      data: {
        ...finance,
        full_name: finance.full_name || (isCompany ? identity.company_name : [identity.legal_first_name, identity.legal_last_name].filter(Boolean).join(" ")),
        buyer_type: finance.buyer_type || identity.buyer_type || order.buyer_type || order.buying_method || order.finance_type,
      },
      fields: [["buyer_type", "Purchase Method"], ["full_name", "Full Name"], ["date", "Signing Date"]],
      documents: [
        // The signed agreement belongs with the VSO in this step.
        ...documents.filter((file) => ["vso", "vso_document", "vso_with_sign", "signature_image"].includes(file.group) || isAgreementGroup(file.group)),
        ...upload(finance, "vso_document", "Signed Vehicle Sales Order"),
        ...upload(order, "vso_document", "Signed Vehicle Sales Order"),
        ...upload(finance, "signature_image", "Signature image"),
      ],
    },
    {
      number: 3, title: "Insurance Request", submitted: completed >= 3 || insuranceSaved || Boolean(submitted.insurance),
      data: insurance,
      fields: [
        ["selling_price", "Selling Price (VSO)"], ["sum_insured_percent", "Sum Insurance Coverage"],
        ["sum_insured_amount", "Sum Insured Amount"], ["current_ncd", "Current NCD"],
        ["windscreen_tinted_coverage", "Windscreen Coverage With Tinted"], ["windscreen_tinted_amount", "Windscreen Coverage Amount"],
        ["insurance_company", "Insurance Company"], ["correspondence_address", "Correspondence Address"],
        ["special_perils", "Special Perils"],
        ["takaful_insurance", "Takaful Insurance"], ["booklet_version", "Insurance Booklet Version"],
      ],
      documents: [
        ...documents.filter((file) => ["ssm", "ncd_letter", "business_name_card", "parking_address"].includes(file.group)),
        ...upload(insurance, "ssm", "Company Full Set SSM"),
        ...upload(insurance, "ncd_letter", "NCD Letter"),
        ...upload(insurance, "business_name_card", "Business Name Card"),
        ...upload(insurance, "parking_address", "Parking Address"),
      ],
    },
  ];
  // A later submitted step also confirms that earlier steps were completed.
  const lastSubmitted = steps.reduce((last, step, index) => step.submitted ? index : last, -1);
  return steps.map((step, index) => ({ ...step, submitted: index <= lastSubmitted }));
}
